import { Link, graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import React from "react"

import Layout from "@components/Layout/Layout"
import RelatedArticles from "@components/RelatedArticles/RelatedArticles"
import { SEO } from "@components/SEO/SEO"

import { Badge } from "@components/ui/badge"
import stripHtml from "../utils/stripHtml"

export default function PostTemplate({
  data: { wpPost: post, allPosts },
  location,
}) {
  // Extract the category slugs of the current post
  const currentPostCategorySlugs = post.categories.nodes.map((cat) => cat.slug)

  // Find up to 3 posts that share a category with the current post
  const relatedPosts = []
  for (let otherPost of allPosts.nodes) {
    if (relatedPosts.length >= 3) {
      break // Stop when 3 articles are found
    }
    if (
      otherPost.id !== post.id && // Exclude the current post
      otherPost.categories.nodes.some((cat) =>
        currentPostCategorySlugs.includes(cat.slug)
      )
    ) {
      relatedPosts.push(otherPost)
    }
  }

  return (
    <Layout lang="fr">
      <div className="relative isolate overflow-hidden bg-gray-700 px-6 py-24 sm:py-36 lg:px-8">
        <GatsbyImage
          image={post.featuredImage.node.gatsbyImage}
          className="!absolute inset-0 -z-10 h-full w-full object-cover mix-blend-overlay"
          alt={post.featuredImage.node.title}
          objectPosition="center 45%"
        />
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
            {post.title}
          </h1>
          <p className="text-md mt-6 line-clamp-2 leading-6 !text-white">
            {stripHtml(post.excerpt)}
          </p>
          <div className="mb-7 flex flex-col justify-center gap-x-5 md:flex-row">
            {post.categories.nodes.length > 0 && (
              <div className="mt-3">
                <div className="flex gap-x-2">
                  {post.categories.nodes.map((cat) => (
                    <Badge key={cat.uri}>{cat.name}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bg-white px-6 py-16 lg:px-8">
        <div className="mx-auto max-w-3xl space-y-10 text-base leading-7 text-gray-700">
          <div className="flex flex-col justify-between gap-y-3 md:flex-row">
            <Link to="/publications" className="text-sm font-light">
              ← Retour aux publications
            </Link>
            <p className="float-right text-sm">Publié le {post.date}</p>
          </div>
          <div
            dangerouslySetInnerHTML={{ __html: post.content }}
            className="flex flex-col gap-y-5 *:list-inside *:list-disc [&_*.size-medium]:w-2/3"
          />
        </div>
      </div>
      <RelatedArticles posts={relatedPosts} />
    </Layout>
  )
}

export const query = graphql`
  query ($id: String) {
    wpPost(id: { eq: $id }) {
      id
      title
      content
      excerpt
      date(formatString: "DD/MM/YYYY")
      featuredImage {
        node {
          title
          gatsbyImage(layout: FULL_WIDTH, width: 3840)
          publicUrl
        }
      }
      categories {
        nodes {
          id
          name
          slug
        }
      }
      tags {
        nodes {
          name
          slug
        }
      }
    }
    allPosts: allWpPost {
      nodes {
        id
        title
        uri
        date(formatString: "DD / MM / YYYY")
        featuredImage {
          node {
            title
            gatsbyImage(layout: CONSTRAINED, width: 960)
          }
        }
        categories {
          nodes {
            slug
          }
        }
      }
    }
  }
`

export const Head = ({ location, data }) => {
  const {
    wpPost: { title, excerpt, date },
  } = data
  return (
    <SEO
      title={`${title} - FEHRGroup`}
      description={stripHtml(excerpt)}
      pathname={location.pathname}
      // image={post.featuredImage.node.publicUrl}
      article
      // lang="fr"
      date={date}
    />
  )
}
